<template>
  <div class="internal-reviews-swiper">
    <swiper-container
      ref="swiper"
      :init.prop="false"
      :slides-per-view.prop="1"
      :space-between.prop="24"
      :observer.prop="true"
      :speed.prop="400"
      :breakpoints.prop="breakpointConfig"
      :navigation.prop="navigationConfig"
      @swiper-init="onInit"
    >
      <swiper-slide v-for="(review, index) in reviewsCopy" :key="`avir-${index}`">
        <div class="rounded-2xl bg-white overflow-hidden card-shadow">
          <div class="flex flex-col bg-gray-100 px-ds-ml py-ds-md gap-ds-md min-h-[180px]">
            <Icon
              :size="23"
              name="i:quote" />
            <div
              :ref="`reviewText-${index}`"
              class="line-clamp italic leading-[140%] lg:leading-[120%] flex-grow"
            >
              <p class="text-rebrandSm lg:text-rebrandMd leading-normal">{{ review.review }}</p>
            </div>

            <div class="flex gap-5">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div class="flex gap-5" v-html="createStarRating(review.rating)" />
              <button
                v-show="review.isOverflowing"
                class="text-black text-opacity-50 hover:text-opacity-100 leading-normal ml-auto"
                @click="showFullReview(index)"
              >
                read more
              </button>
            </div>
          </div>
          <div
            :class="[
              review.date ? 'min-h-[170px]' : 'min-h-[115px]',
              'flex flex-col justify-between p-ds-ml pt-0'
            ]"
          >
            <Icon
              name="i:speech-bubble-connector"
              :size="32"
              class="ml-auto text-gray-100 absolute right-ds-ml"
              style="margin-top: -1px"
            />
            <div class="flex flex-col justify-start flex-grow mt-ds-ml">
              <div class="flex mb-ds-md">
                <div class="text-base lg:text-rebrandSm font-bold">
                  {{ getReviewerDetails(review) }}
                </div>
              </div>
              <div v-if="showItemTypeMoved" class="flex gap-ds-sm items-center mb-ds-sm">
                <span class="text-rebrandMd">{{ review.typeEmoji }}</span>
                <div class="text-base lg:text-rebrandSm content-center">
                  <span
                    >{{ getItemMovedText(review.type) }}
                    <span class="font-bold">{{ review.type }}</span></span
                  >
                </div>
              </div>
              <div v-if="review.date" class="flex gap-ds-sm items-center mb-ds-sm">
                <span class="text-rebrandMd">🗓️</span>
                <div class="text-base lg:text-rebrandSm content-center">
                  {{ $t("summary.on_the") }}
                  <span class="font-bold">{{ formatDate(review.date) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </swiper-slide>
    </swiper-container>
    <div class="swiper-navigation-container">
      <div class="swiper-button-prev" />
      <div class="swiper-button-next" />
    </div>
    <LazyModal
      v-if="showFullReviewModal"
      ref="full-review-modal"
      size="l"
      vertically-centered
      stop-close
      @close="showFullReviewModal = false"
    >
      <h6 class="pb-ds-md mb-ds-md border-b border-black border-opacity-10">
        Anyvan {{ reviewModalData.location }} Review
      </h6>
      <div class="overflow-scroll" style="max-height: 50vh">
        <p class="font-base italic">{{ reviewModalData.review }}</p>
        <div class="font-bold mt-ds-ml">
          {{ getReviewerDetails(reviewModalData) }}
        </div>
      </div>
    </LazyModal>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import "swiper/css"
import "swiper/css/navigation"
import { formatDateWithOrdinal } from "~/scripts/useful-functions"
import { LOCALE } from "~/constants/locale"

interface Review extends InternalReview {
  isOverflowing?: boolean
}

type Article = "a" | "an"

export default defineComponent({
  name: "InternalReviewCarousel",
  props: {
    reviews: {
      required: true,
      type: Array
    }
  },

  setup() {
    const swiper = ref(null)

    onMounted(async () => {
      if (import.meta.client) {
        nextTick(() => {
          setTimeout(() => {
            // I'm not a fan of this solution, but is the most reliable for now
            // Swiper doesn't see custom elements otherwise
            swiper.value?.initialize()
          }, 500)
        })
      }
    })

    return { swiper }
  },

  data() {
    const instanceId = useId()
    return {
      showFullReviewModal: false,
      reviewModalData: {} as Review,
      reviewsCopy: [] as Review[],
      breakpointConfig: {
        768: {
          slidesPerView: 2,
          spaceBetween: 30
        },
        992: {
          slidesPerView: 3,
          spaceBetween: 30
        }
      },
      navigationConfig: {
        nextEl: `.internal-reviews-swiper .swiper-button-next`,
        prevEl: `.internal-reviews-swiper .swiper-button-prev`
      }
    }
  },
  computed: {
    showItemTypeMoved() {
      const supportedLocales = [LOCALE.UK, LOCALE.ES]
      return supportedLocales.includes(this.$i18n.locale)
    }
  },
  created() {
    this.reviewsCopy = JSON.parse(JSON.stringify(this.reviews)) as Review[]
  },

  mounted() {
    window.addEventListener("resize", this.checkOverflow)
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.checkOverflow)
  },
  methods: {
    onInit() {
      this.$nextTick(() => {
        // make slider visible after it's initialized to avoid flicker
        const swiperWrapperEl = document.querySelector(".internal-reviews-swiper")
        if (swiperWrapperEl) {
          ;(swiperWrapperEl as HTMLElement).style.height = "auto"
        }
        this.checkOverflow()
      })
    },
    checkOverflow(): void {
      this.$nextTick(() => {
        this.reviewsCopy.forEach((review, index) => {
          const el = this.$refs[`reviewText-${index}`] as HTMLElement[]
          if (el && el[0]) {
            review.isOverflowing = el[0].scrollHeight > el[0].clientHeight
          }
        })
      })
    },
    showFullReview(index: number) {
      this.reviewModalData = this.reviewsCopy[index]
      this.showFullReviewModal = true
    },
    formatDate(date: string): string {
      const currentLocale = this.$i18n.locales.value.find(
        (locale: string) => locale.code === this.$i18n.locale
      )
      return formatDateWithOrdinal(new Date(date), currentLocale.language, this.$i18n.locale)
    },
    createStarRating(rating: number) {
      let starsDiv = ""
      // starsDiv.className = "flex gap-ds-xxxs"
      const fullStars = Math.floor(rating)
      const hasHalfStar = rating % 1 !== 0

      // add full stars
      starsDiv +=
        `<img class='internalReviewStar' loading="lazy" src='/content-static/icons/star-hard-yellow.svg'/>`.repeat(
          fullStars
        )

      if (hasHalfStar) {
        starsDiv += `<img class='internalReviewStar' loading="lazy" src='/content-static/icons/half-star-hard-yellow.svg'/>`
      }

      return starsDiv
    },
    getItemMovedText(text: string): string {
      const article = this.$i18n.locale !== LOCALE.ES ? this.getArticle(text) : ""
      const translation = this.$t("summary.moved")
      return article ? `${translation} ${article} ` : translation
    },
    getArticle(text: string): Article {
      if (!text || typeof text[0] !== "string") return "a"
      const firstChar = text[0].toLowerCase()
      return ["a", "e", "i", "o", "u"].includes(firstChar) ? "an" : "a"
    },
    getReviewerDetails(review: { location: string; name: string | null }): string {
      if (this.$i18n.locale === LOCALE.US) {
        return review.name || ""
      }
      return `${review.name ? review.name + ", " : ""}${review.location}`
    }
  }
})
</script>

<style lang="scss" module>
:global {
  .av-internal-review-section,
  .av-go-local-reviews {
    .swiper-slide {
      transition:
        opacity 0.3s ease,
        visibility 0.3s ease;

      &:not(.swiper-slide-visible) {
        @apply opacity-0 invisible;
      }
    }

    .swiper-navigation-container {
      @apply flex gap-ds-lg items-center mt-ds-ml mb-ds-sm justify-between md:justify-end z-[1];

      .swiper-button-prev,
      .swiper-button-next {
        @apply relative z-[1] min-h-ds-xl min-w-ds-xl h-ds-xl w-ds-xl cursor-pointer flex items-center justify-center rounded-full text-black border border-solid border-black border-opacity-20 bg-white inset-auto mt-auto;

        &:after {
          @apply text-base;
        }
      }
    }
  }

  .line-clamp {
    display: -webkit-box;
    line-clamp: 4;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .card-shadow {
    box-shadow: 0px 2px 15px 0px rgba(0, 0, 0, 0.1);
  }
  .internalReviewStar {
    width: 18px !important;
    height: 18px !important;
  }
}
</style>
